import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
  Typography,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const PurchasePaymentEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/purchase-payments",
    token
  );

  const { data: purchasesData, loading: purchaseLoading } = useFetchObjects(
    ["purchase"],
    "shop/purchases",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const purchases = Array.isArray(purchasesData) ? purchasesData : purchasesData?.results || [];
  const currencies = Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || [];

  const [selectedPurchase, setSelectedPurchase] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(""); // Add payment date state
  const [notes, setNotes] = useState(""); // Add notes state
  const [purchaseDetails, setPurchaseDetails] = useState(null);

  useEffect(() => {
    if (editData) {
      setAmount(editData.amount);
      setPaymentDate(editData.payment_date.slice(0, 16)); // Set payment date to match the format
      setNotes(editData.notes || ""); // Set notes from editData
      const purchaseId =
        editData.purchase && editData.purchase.id
          ? String(editData.purchase.id)
          : editData.purchase
          ? String(editData.purchase)
          : "";
      setSelectedPurchase(purchaseId);

      const currencyId =
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : "";
      setSelectedCurrency(currencyId);
    }
  }, [editData]);

  useEffect(() => {
    if (purchases && selectedPurchase) {
      const selectedPurchaseDetails = purchases.find(
        (purchase) => purchase.id === selectedPurchase
      );
      if (selectedPurchaseDetails) {
        setPurchaseDetails(selectedPurchaseDetails);
        setSelectedCurrency(selectedPurchaseDetails.currency_details?.id || "");
      }
    }
  }, [selectedPurchase, purchases]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedPurchase("");
      setSelectedCurrency("");
      setAmount("");
      setPaymentDate(""); // Reset payment date
      setNotes(""); // Reset notes
      onClose();
    }
  }, [isSuccess, onClose]);

  const handleSubmit = () => {
    const payload = {
      purchase: selectedPurchase,
      currency: selectedCurrency,
      amount,
      payment_date: paymentDate, // Include payment date
      notes: notes, // Include notes
    };
    handleUpdate(editData.id, payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("purchasePayment.editPurchasePayment")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectPurchase")}
            fullWidth
            required
            value={selectedPurchase}
            onChange={(e) => setSelectedPurchase(e.target.value)}
          >
            {purchaseLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (purchases || []).map((purchase) => (
                <MenuItem key={purchase.id} value={purchase.id}>
                  <strong>Purchase #{purchase.id}</strong> - {purchase.supplier_details?.name || 'N/A'}
                </MenuItem>
              ))
            )}
          </TextField>

          {purchaseDetails && (
            <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                <strong>{t("purchasePayment.totalAmount")}:</strong>{" "}
                {purchaseDetails.total_price?.[purchaseDetails.currency_details?.title] || 0}{" "}
                {purchaseDetails.currency_details?.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={1}>
                <strong>{t("purchasePayment.totalPaid")}:</strong>{" "}
                {purchaseDetails.total_paid?.[purchaseDetails.currency_details?.title] || 0}{" "}
                {purchaseDetails.currency_details?.title}
              </Typography>
              <Typography variant="body2" color="error" fontWeight="bold">
                <strong>{t("purchasePayment.totalRemaining")}:</strong>{" "}
                {purchaseDetails.total_remained_amount?.[purchaseDetails.currency_details?.title] || 0}{" "}
                {purchaseDetails.currency_details?.title}
              </Typography>
            </Box>
          )}

          <TextField
            select
            margin="dense"
            label={t("purchasePayment.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((curr) => (
                <MenuItem key={curr.id} value={curr.id}>
                  {curr.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("purchasePayment.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          {/* Payment Date Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.paymentDate")}
            type="date"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)} // Handle date change
          />

          {/* Notes Input */}
          <TextField
            margin="dense"
            label={t("purchasePayment.notes")}
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchasePayment.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedPurchase || !selectedCurrency || !amount
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchasePayment.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchasePaymentEdit;
